/**
 * The over as chips, and every chip a way into correcting that ball.
 *
 * `MatchState.header.currentOver` is already exactly this list, in effective-ledger order, so
 * the strip is a map over it and nothing else. Deriving "which balls are in this over" here
 * would be a second traversal, and the two would drift the first time a correction landed
 * (ADR-0006).
 *
 * Two markers, and they mean different things, so they do not share a colour:
 *
 * - **Edited** is an audit fact — this ball was corrected, by someone, at some time. ADR-0004
 *   asks for it to be subtle, and it is a corner dot rather than a colour change because the
 *   chip's colour already says whether the ball was runs, an extra or a wicket, and trading
 *   that for an audit signal would lose a cricket fact to gain a bookkeeping one.
 * - **Flagged** is the engine saying this ball is illegal cricket. It is accepted anyway and
 *   blocks completion until a human looks (ADR-0011), so it takes the wicket colour and the
 *   `title` carries the reason.
 *
 * Both come off the entry. The engine puts the anomaly *on the chip* precisely so this
 * component does not have to join `state.anomalies` by id.
 */
import { formatBallChip } from "@sportsos/engine";
import type { EventId, OverStripEntry } from "@sportsos/engine";

/**
 * Map each delivery to the design-system marker colour variant.
 *
 * The colour mapping encodes cricket meaning, not decoration:
 * - Wicket (magenta): the outcome that matters most
 * - Boundary 4 (teal): a scoring shot to the fence
 * - Six (violet): over the rope
 * - Extras (amber): wide, no-ball, bye, leg-bye — not off the bat
 * - Dot or ordinary runs: neutral (no modifier)
 */
const markerVariant = (entry: OverStripEntry): string => {
  if (entry.wicket) return " ds-marker--magenta";
  if (!entry.legal || entry.runs.extras.bye > 0 || entry.runs.extras.legbye > 0)
    return " ds-marker--amber";
  if (entry.runs.batter === 6) return " ds-marker--violet";
  if (entry.runs.batter === 4) return " ds-marker--teal";
  return "";
};

const chipClass = (entry: OverStripEntry): string => {
  const base = `ds-marker ds-marker--sm${markerVariant(entry)}`;
  // Flagged outranks edited when a ball is both: an unresolved anomaly is the one that stops
  // the match being completed, so it is the one worth a scorer's attention first.
  if (entry.anomaly !== null) return `${base} flagged`;
  return entry.edited === null ? base : `${base} edited`;
};

/** What the marker means, for a long-press or a hover. Never the only way to find out. */
function chipTitle(entry: OverStripEntry): string | undefined {
  if (entry.anomaly !== null) return entry.anomaly;
  if (entry.edited === null) return undefined;
  return `Edited ${entry.edited.revisions === 1 ? "once" : `${entry.edited.revisions} times`}`;
}

export function OverStrip({
  balls,
  overNo,
  onTapBall,
  onOpenLedger,
}: {
  readonly balls: readonly OverStripEntry[];
  readonly overNo: number;
  /**
   * Open the correction sheet for this ball.
   *
   * Absent on the match detail view, which is somebody *reading* a match — possibly one he
   * did not score and has no lease on. The chips then render as text rather than as disabled
   * buttons, because a control that cannot do anything is worse than no control: it still
   * takes a tap to find that out, and a screen reader still announces it as actionable.
   */
  readonly onTapBall?: ((eventId: EventId) => void) | undefined;
  /**
   * Open every ball of the innings. Absent inside the ledger itself, which is already it.
   *
   * On the over label because that label is in the read half and therefore *always* visible —
   * the pad is not. At an over boundary the bottom half is asking for the next bowler, and
   * that is precisely the moment a scorer realises the over just bowled was recorded against
   * the wrong man; routing this through the pad's "…more" would make it unreachable exactly
   * when it is wanted.
   */
  readonly onOpenLedger?: (() => void) | undefined;
}): React.ReactElement {
  return (
    <div className="row" style={{ marginTop: "var(--ds-space-3)", gap: "var(--ds-space-2)", overflow: "hidden" }}>
      {onOpenLedger === undefined ? (
        <span className="ds-label" style={{ flex: "none" }}>Ov {overNo + 1}</span>
      ) : (
        <button className="ds-label" style={{ flex: "none", background: "none", border: 0, padding: 0, cursor: "pointer", font: "inherit", letterSpacing: "inherit", textTransform: "inherit", color: "inherit" }} onClick={onOpenLedger} aria-label="Show every ball this innings">
          Ov {overNo + 1} ▾
        </button>
      )}
      {balls.length === 0 ? (
        <span className="ds-label">
          <i>no balls yet</i>
        </span>
      ) : (
        balls.map((entry) => {
          // The over and ball, not the chip's shorthand: "0.3" is what a scorer says out
          // loud, and a screen reader announcing "1" for a single is announcing the runs
          // without saying which ball they were.
          const label = `Over ${entry.over + 1} ball ${entry.ballInOver}, ${formatBallChip(entry)}${
            entry.anomaly === null ? "" : `, flagged: ${entry.anomaly}`
          }${entry.edited === null ? "" : ", edited"}`;

          return onTapBall === undefined ? (
            <span
              className={chipClass(entry)}
              key={entry.eventId}
              title={chipTitle(entry)}
              aria-label={label}
            >
              {formatBallChip(entry)}
            </span>
          ) : (
            <button
              type="button"
              className={chipClass(entry)}
              key={entry.eventId}
              title={chipTitle(entry)}
              aria-label={`${label}. Tap to correct.`}
              onClick={() => onTapBall(entry.eventId)}
              style={{ cursor: "pointer" }}
            >
              {formatBallChip(entry)}
            </button>
          );
        })
      )}
    </div>
  );
}

/**
 * Every ball of the innings, grouped by over.
 *
 * The scorer may be in over 14 and realise ball 2 of over 7 was wrong (PRD §6.7), and the
 * current-over strip cannot reach it. `InningsState.deliveries` is already every ball in
 * effective order — added for exactly this — so this is a grouping and nothing more.
 */
export function LedgerView({
  balls,
  onTapBall,
}: {
  readonly balls: readonly OverStripEntry[];
  readonly onTapBall: (eventId: EventId) => void;
}): React.ReactElement {
  const overs = [...new Set(balls.map((entry) => entry.over))].sort((a, b) => b - a);

  return (
    <div className="stack--tight">
      {overs.length === 0 ? <span className="ds-label">No balls yet</span> : null}
      {/* Newest over first: the balls a scorer is most likely to be fixing are the recent ones,
          and a long innings would otherwise put them past the scroll. */}
      {overs.map((overNo) => (
        <div key={overNo}>
          <OverStrip
            balls={balls.filter((entry) => entry.over === overNo)}
            overNo={overNo}
            onTapBall={onTapBall}
          />
        </div>
      ))}
    </div>
  );
}
